import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import React, { FC } from 'react';
import { FormCheckBox } from 'src/components/FormPart/FormCheckBox';
import { FormDropdown } from 'src/components/FormPart/FormDropdown';
import { FormComponentProps } from 'src/components/FormPart/types';
import { SelectOption } from 'src/UIKit';
import { VettingOption } from 'src/api/campaignRegistry';

export type VettingType = 'STANDARD' | 'ENHANCED';
export type VettingPartner = 'Aegis Mobile' | string;

export type ApplyForVettingFormData = {
  vettingType: VettingType;
  vettingPartner: VettingPartner;
  chargeForVetting: boolean;
};

type ApplyForVettingFormProps = {
  submitting?: boolean;
  vettingOptions: VettingOption[];
  optionsLoading: boolean;
};

export const ApplyForVettingForm: FC<
  FormComponentProps<ApplyForVettingFormData, ApplyForVettingFormProps>
> = (props) => {
  const { form, submitting = false, vettingOptions, optionsLoading } = props;

  const vettingType = form.watch('vettingType');
  const vettingPartner = form.watch('vettingPartner');
  const chargeForVetting = form.watch('chargeForVetting');

  const vettingTypes: SelectOption<VettingType, string>[] = Array.from(
    new Set(vettingOptions.map(option => option.vettingClass)),
  ).map(vettingClass => ({
    value: vettingClass as VettingType,
    title: vettingClass,
  }));

  const vettingPartners: SelectOption<VettingPartner, string>[] = Array.from(
    new Set(vettingOptions.map(option => option.providerName)),
  ).map(providerName => ({
    value: providerName as VettingPartner,
    title: providerName,
  }));

  const selectedVettingOption = vettingOptions.find(
    option => option.vettingClass === vettingType && option.providerName === vettingPartner,
  );
  const selectedPrice = selectedVettingOption?.priceUsd;
  const price = selectedPrice != null ? `$${selectedPrice.toFixed(2)}` : 'N/A';
  const estimatedTime = selectedVettingOption != null
    ? `Up to ${selectedVettingOption.estimatedBusinessDays} business days.`
    : 'N/A';

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormDropdown
          form={form}
          fieldName="vettingType"
          label="Select Vetting Type"
          placeholder="Select Vetting Type"
          options={vettingTypes}
          validation={{
            required: 'Vetting Type is required',
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormDropdown
          form={form}
          fieldName="vettingPartner"
          label="Select Vetting Partner"
          placeholder="Select Vetting Partner"
          options={vettingPartners}
          validation={{
            required: 'Vetting Partner is required',
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormCheckBox
          form={form}
          fieldName="chargeForVetting"
          label="Charge for vetting price"
        />
      </Grid>

      <Grid item xs={12}>
        <Box mt={1}>
          {optionsLoading && (
            <Typography variant="body2" color="text.secondary" mb={1}>
              Loading vetting options...
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" mb={1}>
            <strong>Vetting Price:</strong> {price} <br />
            {`The vetting charge will appear on the company’s next bill${chargeForVetting ? '.' : ' WITH 100% DISCOUNT.'}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Estimated Time for Vetting:</strong> {estimatedTime} <br />
            Time varies based on vetting provider capacity.
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} display="flex" justifyContent="flex-end">
        <Button
          type="submit"
          disabled={!form.formState.isValid || submitting || optionsLoading || vettingOptions.length === 0}
          variant="contained"
          color="primary"
          size="large"
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </Grid>
    </Grid>
  );
};
